import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useContext, useMemo, useRef, useState } from "react";
import { Linking, ScrollView, StyleSheet, Text, View } from "react-native";
import {
  RichEditor,
  RichToolbar,
  actions,
} from "react-native-pell-rich-editor";
import WebView from "react-native-webview";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.color.background,
    marginTop: 10,
    marginBottom: 10,
  },
  titleText: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    marginBottom: 5,
    marginLeft: 6,
  },
  requiredText: {
    color: theme.color.error,
  },
});

function ToolbarIcon(props) {
  const { name } = props;

  return <Icon name={name} size={20} color={theme.color.darkGrey} />;
}

const actionsIcons = {
  [actions.heading2]: () => <ToolbarIcon name="format-header-2" />,
  [actions.setBold]: () => <ToolbarIcon name="format-bold" />,
  [actions.setItalic]: () => <ToolbarIcon name="format-italic" />,
  [actions.insertBulletsList]: () => (
    <ToolbarIcon name="format-list-bulleted" />
  ),
  [actions.insertOrderedList]: () => (
    <ToolbarIcon name="format-list-numbered" />
  ),
  [actions.undo]: () => <ToolbarIcon name="undo" />,
  [actions.redo]: () => <ToolbarIcon name="redo" />,
};

export default function CustomTextEditor(props) {
  const {
    readOnly,
    disabled,
    initialText,
    border,
    title,
    placeholder,
    required,
    isLoadingCallback,
    onChangeText,
  } = props;

  const { organizationColor } = useContext(OrganizationContext);

  const [editorHeight, setEditorHeight] = useState(0);

  const richTextRef = useRef(null);

  const [elementId] = useState(
    () => `editor-${Math.random().toString(36).substring(2, 9)}`,
  );

  const preHtmlContent = `<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<style>
  * { color: ${theme.color.black}; font-family: sans-serif; font-size: ${theme.font.size.textEditorBody}px; }
  a { color: ${theme.color.link}; }
  h1, h2, h3 { font-size: ${theme.font.size.subHeader}px; }
</style>
<script>
  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("a").forEach(function (anchor) {
      anchor.addEventListener("click", function (event) {
        event.preventDefault();
        window.parent.postMessage(
          JSON.stringify({
            id: "${elementId}",
            type: "externalLink",
            href: anchor.href,
          }),
        );
      });
    });

    window.parent.postMessage(
      JSON.stringify({
        id: "${elementId}",
        type: "height",
        value: document.documentElement.scrollHeight,
      }),
    );
  });
</script>`;

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        borderWrapper: {
          borderRadius: 8,
          borderWidth: 2,
          borderColor: organizationColor,
          zIndex: 1,
          opacity: disabled ? theme.opacity.disabled : 1,
          backgroundColor: theme.color.background,
          boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.25)",
        },
        editor: {
          borderTopLeftRadius: border ? 6 : 0,
          borderTopRightRadius: border ? 6 : 0,
          borderRadius: border && readOnly ? 6 : 0,
          backgroundColor: theme.color.background,
          minHeight: 50,
          maxHeight: 150,
          paddingTop: 6,
        },
        editorToolbar: {
          borderBottomLeftRadius: border ? 6 : 0,
          borderBottomRightRadius: border ? 6 : 0,
        },
        readOnlyContainerStyle: {
          width: "100%",
          height: editorHeight,
        },
      }),
    [organizationColor, disabled, border, readOnly, editorHeight],
  );

  if (readOnly) {
    return (
      <WebView
        style={stateClasses.readOnlyContainerStyle}
        source={{ html: `${preHtmlContent} ${initialText}` }}
        scalesPageToFit={false}
        onLoad={() => isLoadingCallback && isLoadingCallback(false)}
        onMessage={(event) => {
          try {
            const data =
              typeof event.nativeEvent.data === "string"
                ? JSON.parse(event.nativeEvent.data)
                : event.nativeEvent.data;

            if (data.id === elementId) {
              if (data.type === "height") {
                setEditorHeight(data.value);
              } else if (data.type === "externalLink") {
                Linking.openURL(data.href).catch((err) => {
                  if (__DEV__) {
                    console.error("Failed to open link", err);
                  }
                });
              }
            }
          } catch (err) {
            if (__DEV__) {
              console.warn("Failed to parse message", err);
            }
          }
        }}
      />
    );
  }

  return (
    <View style={classes.root}>
      {title && (
        <Text style={classes.titleText}>
          {title}
          {required && (
            <Text style={!disabled && classes.requiredText}> *</Text>
          )}
        </Text>
      )}

      <View style={border ? stateClasses.borderWrapper : null}>
        <View style={stateClasses.editor}>
          <ScrollView>
            <RichEditor
              initialContentHTML={initialText || ""}
              placeholder={placeholder}
              ref={richTextRef}
              onChange={onChangeText}
              editorStyle={{
                color: theme.color.darkGrey,
                backgroundColor: theme.color.background,
              }}
              editorInitializedCallback={() =>
                isLoadingCallback && isLoadingCallback(false)
              }
              testID={`text-editor-${title}`}
            />
          </ScrollView>
        </View>
        <View>
          <RichToolbar
            editor={richTextRef}
            style={stateClasses.editorToolbar}
            actions={Object.keys(actionsIcons)}
            iconMap={actionsIcons}
          />
        </View>
      </View>
    </View>
  );
}
